import { useState } from "react";

import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import { Badge, IconButton, Popover, Tooltip, Zoom } from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { useNavigate } from "react-router-dom";

import { ClientAppEvent, EVOTRUX_URL_HASH } from "constants/index";
import { useChatUnreadContext } from "context/ChatUnreadContext";
import { useRoleContext } from "hooks";

import { ChatInboxPanel } from "./ChatInboxPanel";

import type { MouseEvent } from "react";

import type { InboxChat } from "context/ChatUnreadContext";

const POPOVER_WIDTH = 500;
const POPOVER_VIEWPORT_OFFSET_PX = 80;

export function ChatInboxPopoverButton() {
  const { role } = useRoleContext();
  const { unreadCount } = useChatUnreadContext();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasUnread = unreadCount > 0;

  if (!role) return <div />;

  const isOpen = anchorEl !== null;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRowClick = (row: InboxChat) => {
    posthog?.capture(ClientAppEvent.CHAT_INBOX_ROW_OPENED, {
      channel_name: row.channel_name,
      shipment_id: row.shipment_id,
      unread_count_at_click: row.unread_count,
    });
    setAnchorEl(null);
    navigate(`/shipments/${row.shipment_id}${EVOTRUX_URL_HASH.LIVE_CHAT}`);
  };

  const icon = hasUnread ? (
    <Badge color="error" badgeContent={unreadCount} max={99} overlap="circular">
      <ChatBubbleIcon fontSize="large" />
    </Badge>
  ) : (
    <ChatBubbleIcon fontSize="large" />
  );

  return (
    <>
      <Tooltip
        title={hasUnread ? `Chats (${unreadCount} unread)` : "Chats"}
        placement="right"
        TransitionComponent={Zoom}
      >
        <IconButton
          color="inherit"
          onClick={handleOpen}
          sx={{
            transition: "background-color 0.15s, transform 0.15s",
            ...(hasUnread && {
              bgcolor: "rgba(255, 255, 255, 0.12)",
              animation: "chatInboxPulse 2s ease-in-out infinite",
            }),
            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.18)" },
            "@keyframes chatInboxPulse": {
              "0%, 100%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.08)" },
            },
          }}
        >
          {icon}
        </IconButton>
      </Tooltip>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: POPOVER_WIDTH,
              height: `calc(100vh - ${POPOVER_VIEWPORT_OFFSET_PX}px)`,
              ml: 1,
              display: "flex",
              flexDirection: "column",
              borderRadius: 1,
            },
          },
        }}
      >
        <ChatInboxPanel isOpen={isOpen} onRowClick={handleRowClick} />
      </Popover>
    </>
  );
}
